import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CreditCard, Monitor, Globe, ShoppingCart, Smartphone, Receipt, Banknote } from "lucide-react";
import { fadeUp, stagger, viewportOnce } from "@/utils/motionVariants";

const ALL_SOLUTIONS = {
  "/card-machines": { title: "Card Machines", desc: "Countertop, mobile, and smart terminals with next-day delivery.", icon: CreditCard },
  "/epos-systems": { title: "EPOS Systems", desc: "Till and card payments combined for hospitality and retail.", icon: Monitor },
  "/payment-gateway": { title: "Payment Gateway", desc: "Accept payments online with enterprise-grade security.", icon: Globe },
  "/online-payments": { title: "Online Payments", desc: "Secure online card processing for e-commerce businesses.", icon: ShoppingCart },
  "/virtual-terminal": { title: "Phone Payments", desc: "Take payments over the phone with a virtual terminal.", icon: Smartphone },
  "/switch-and-save": { title: "Switch and Save", desc: "Compare your current setup and see if you could pay less.", icon: Receipt },
  "/merchant-cash-advance": { title: "Merchant Cash Advance", desc: "Flexible funding based on your card turnover.", icon: Banknote },
  "/partners/integrations": { title: "E-Commerce Integrations", desc: "WooCommerce, Shopify, Magento, and more.", icon: ShoppingCart },
  "/partner-programme": { title: "Partner Programme", desc: "Earn recurring income by referring businesses.", icon: Receipt },
};

const SOLUTIONS_MAP = {
  "/switch-and-save": ["/card-machines", "/epos-systems", "/payment-gateway"],
  "/cost-of-cash": ["/card-machines", "/epos-systems", "/switch-and-save"],
  "/cash-vs-card": ["/card-machines", "/epos-systems", "/online-payments"],
  "/quote/in-person": ["/card-machines", "/epos-systems", "/switch-and-save"],
  "/quote/online": ["/payment-gateway", "/online-payments", "/partners/integrations"],
  "/overpaying-scorecard": ["/switch-and-save", "/card-machines", "/payment-gateway"],
  "/hidden-fees-checker": ["/switch-and-save", "/card-machines", "/epos-systems"],
  "/switch-vs-stay": ["/switch-and-save", "/card-machines", "/payment-gateway"],
  "/statement-review": ["/switch-and-save", "/card-machines", "/payment-gateway"],
  "/payment-finder": ["/card-machines", "/epos-systems", "/payment-gateway"],
  "/epos-partner-calculator": ["/epos-systems", "/partner-programme", "/partners/integrations"],
  "/ecom-partner-calculator": ["/payment-gateway", "/partner-programme", "/partners/integrations"],
};

export default function SolutionsCrossSell({ currentPath }) {
  const related = SOLUTIONS_MAP[currentPath];
  if (!related) return null;

  const solutions = related.map((path) => ({ path, ...ALL_SOLUTIONS[path] })).filter((s) => s.title);
  if (solutions.length === 0) return null;

  return (
    <section className="py-14 bg-white" data-testid="solutions-cross-sell">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-2">Explore our solutions</p>
            <h3 className="font-heading text-2xl font-bold text-brand-black tracking-tight">Products that could work for you</h3>
          </motion.div>
          <motion.div variants={stagger} className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {solutions.map((sol) => {
              const Icon = sol.icon;
              return (
                <motion.div key={sol.path} variants={fadeUp}>
                  <Link to={sol.path} className="group block bg-gray-50 border border-brand-light-grey rounded-2xl p-5 hover:border-brand-aqua hover:bg-white hover:shadow-md hover:-translate-y-0.5 transition-all duration-300" data-testid={`solution-${sol.path.replace(/\//g, "-").slice(1)}`}>
                    <div className="w-9 h-9 rounded-lg bg-brand-aqua flex items-center justify-center mb-3">
                      <Icon size={18} className="text-white" />
                    </div>
                    <h4 className="font-heading text-sm font-bold text-brand-black mb-1">{sol.title}</h4>
                    <p className="text-xs text-brand-grey leading-relaxed">{sol.desc}</p>
                    <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-aqua group-hover:gap-2 transition-all">Learn more <ArrowRight size={12} /></span>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
